import { useState } from "react";
import logoBTGNewsletter from "@assets/logo-btg-newsletter.svg";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterForm from "@features/newsletter/components/NewsletterForm";
import { Transition } from "@headlessui/react";
import { useWindowSize } from "@hooks/useWindowSize";
import { cn } from "@util/helpers";

export type BTGlobalNewsletterProps = {
  /** The classname of the component. */
  className?: string;
  /** The section name to be added to the data-section-name attribute. */
  dataSectionName?: string;
  /** Callback after clicking sign up button */
  onSubmitCallback?: () => void;
  /** Callback after successful sign up */
  onSuccessCallback?: () => void;
};

/**
 * BT Global Newsletter component to subscribe to global newsletter which is use in /global page
 * Reference:
 *   - transition classes: https://www.skies.dev/headless-ui-transitions
 * */
const BTGlobalNewsletter = ({
  className,
  dataSectionName,
  onSubmitCallback,
  onSuccessCallback,
}: BTGlobalNewsletterProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const [isShowForm, setIsShowForm] = useState(false);

  const btGlobalNewsletter = BT_NEWSLETTERS.find(
    (newsletter) => newsletter.type === NEWSLETTER_TYPE.BTGLOBAL
  );

  if (typeof btGlobalNewsletter === "undefined") return <></>;

  const transitionClasses = {
    enter: "transition ease-linear duration-200",
    enterFrom: "opacity-0 -translate-y-1",
    enterTo: "opacity-100 translate-y-0",
    leave: "transition ease-in duration-150",
    leaveFrom: "opacity-100 translate-y-0",
    leaveTo: "opacity-0 -translate-y-1",
  };

  const handleShowForm = () => {
    setIsShowForm(true);
  };

  return (
    <div
      data-testid="bt-global-newsletter-component"
      data-section-name={dataSectionName}
      className={cn(
        "track-impression mx-auto flex w-full flex-col items-center justify-between border-y border-blue-50 py-4 lg:w-10/12 lg:flex-row lg:border-none lg:px-15 xl:w-3/4",
        className
      )}
    >
      <div className="flex items-center justify-center gap-6 pb-3 lg:pb-0">
        <img
          className="relative z-10"
          src={logoBTGNewsletter}
          width={72}
          height={72}
          alt=""
        />

        <p className="font-poppins text-base font-medium leading-normal text-gray-750">
          {btGlobalNewsletter.description}

          {!isScreenLG ? (
            <span
              className="ml-1 font-poppins text-xs font-medium leading-normal text-verticals-btblue underline"
              onClick={handleShowForm}
            >
              Signup »
            </span>
          ) : null}
        </p>
      </div>

      <Transition
        show={isScreenLG || isShowForm}
        as="div"
        className="w-full lg:w-auto"
        {...transitionClasses}
      >
        <NewsletterForm
          className="group w-full transition duration-300 ease-in data-[closed]:opacity-0 lg:w-auto lg:min-w-[360px]"
          buttonClassName={cn(
            "px-5 py-2 flex items-center justify-center text-verticals-btblue hover:opacity-75 border border-blue-50 w-auto"
          )}
          formClassName={cn("py-2 border-b border-gray-350")}
          inputClassName={cn("rounded-full px-4 placeholder:text-blue-170")}
          newsletters={[btGlobalNewsletter]}
          isBordered={false}
          placeholder="Your email address"
          signUpButtonIcon={
            <IconFactory
              type={IconTypeEnum.rightArrow}
              className="text-verticals-btblue"
            />
          }
          onSubmitCallback={onSubmitCallback}
          onSuccessCallback={onSuccessCallback}
        />
      </Transition>
    </div>
  );
};

export default BTGlobalNewsletter;
